import { FC, useMemo } from "react";
import { ResponsiveBar } from "@nivo/bar";
import { useTheme } from "@mui/material/styles";

export type OrgCountBarChartItem = {
  org: string;
  count: number;
  [key: string]: string | number;
};

export type OrgCountBarChartSeries = {
  key: string;
  label: string;
  color: string;
};

type OrgCountBarChartProps = {
  data: OrgCountBarChartItem[];
  valueLabel: string;
  color?: string;
  series?: OrgCountBarChartSeries[];
};

const formatOrgLabel = (name: string) =>
  name.replace(/^BluePearl\s+/i, "").trim();

const HORIZONTAL_LAYOUT_THRESHOLD = 5;

export const OrgCountBarChart: FC<OrgCountBarChartProps> = ({
  data,
  valueLabel,
  color,
  series,
}) => {
  const theme = useTheme();
  const barColor = color ?? theme.palette.primary.main;
  const chartSeries = series ?? [{ key: "count", label: valueLabel, color: barColor }];
  const chartKeys = chartSeries.map((item) => item.key);
  const chartColors = chartSeries.map((item) => item.color);
  const seriesLabels = Object.fromEntries(
    chartSeries.map((item) => [item.key, item.label]),
  );

  const useHorizontalLayout = data.length > HORIZONTAL_LAYOUT_THRESHOLD;
  const longestOrgLabel = useMemo(
    () =>
      data.reduce(
        (max, item) => Math.max(max, formatOrgLabel(item.org).length),
        0,
      ),
    [data],
  );

  const chartHeight = useHorizontalLayout
    ? Math.max(360, data.length * 36 + (series ? 80 : 60))
    : 360;

  const margin = useHorizontalLayout
    ? {
        top: series ? 48 : 16,
        right: 24,
        bottom: 52,
        left: Math.min(220, Math.max(100, longestOrgLabel * 7 + 24)),
      }
    : {
        top: series ? 48 : 20,
        right: 20,
        bottom: 56,
        left: 60,
      };

  const seriesLegend = series
    ? [
        {
          dataFrom: "keys" as const,
          anchor: "top-left" as const,
          direction: "row" as const,
          justify: false,
          translateX: 0,
          translateY: -40,
          itemsSpacing: 16,
          itemWidth: 120,
          itemHeight: 20,
          itemDirection: "left-to-right" as const,
          symbolSize: 12,
          symbolShape: "circle" as const,
        },
      ]
    : undefined;

  if (data.length === 0) {
    return null;
  }

  return (
    <div style={{ height: chartHeight, width: "100%" }}>
      <ResponsiveBar
        data={data}
        keys={chartKeys}
        indexBy="org"
        layout={useHorizontalLayout ? "horizontal" : "vertical"}
        groupMode="grouped"
        margin={margin}
        padding={useHorizontalLayout ? 0.25 : 0.35}
        innerPadding={4}
        valueScale={{ type: "linear" }}
        indexScale={{ type: "band", round: true }}
        colors={chartColors}
        borderRadius={4}
        axisTop={null}
        axisRight={null}
        axisBottom={
          useHorizontalLayout
            ? {
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: valueLabel,
                legendPosition: "middle",
                legendOffset: 40,
              }
            : {
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                format: formatOrgLabel,
              }
        }
        axisLeft={
          useHorizontalLayout
            ? {
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                format: formatOrgLabel,
              }
            : {
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: valueLabel,
                legendPosition: "middle",
                legendOffset: -50,
              }
        }
        labelSkipWidth={12}
        labelSkipHeight={12}
        legends={seriesLegend}
        role="application"
        ariaLabel={`${valueLabel} by organization`}
        tooltip={({ indexValue, id, value }) => (
          <div
            style={{
              padding: "8px 12px",
              background: "white",
              borderRadius: "4px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
            }}
          >
            <strong>{indexValue}</strong>: {value}{" "}
            {String(seriesLabels[String(id)] ?? valueLabel).toLowerCase()}
          </div>
        )}
        animate
      />
    </div>
  );
};
